import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const DomainSettings = () => {
  const [customDomain, setCustomDomain] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  const defaultDomain = 'ecommerce-store.appcreator.app';
  const sslStatus = 'active';

  const dnsRecords = [
    {
      type: 'A',
      name: '@',
      value: '76.76.21.21',
      status: 'verified'
    },
    {
      type: 'CNAME',
      name: 'www',
      value: 'cname.appcreator.app',
      status: 'verified'
    }
  ];

  const handleVerifyDomain = () => {
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      setVerificationStatus('success');
    }, 2000);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          Default Domain
        </h3>
        
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-muted rounded-lg">
            <div className="flex-1 min-w-0">
              <p className="caption font-medium text-foreground mb-1">Current Domain</p>
              <p className="text-sm text-primary font-mono break-all">{defaultDomain}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => window.open(`https://${defaultDomain}`, '_blank')}
            >
              Visit
            </Button>
          </div>

          <div className="flex items-start gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success mt-0.5 flex-shrink-0" />
            <div>
              <p className="caption font-medium text-success">SSL Certificate Active</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your site is secured with HTTPS encryption
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          Custom Domain
        </h3>
        
        <div className="space-y-4 md:space-y-5">
          <div className="space-y-3">
            <Input
              label="Domain Name"
              type="text"
              placeholder="example.com"
              value={customDomain}
              onChange={(e) => setCustomDomain(e?.target?.value)}
              description="Enter your custom domain without http:// or https://"
            />
            
            <Button
              variant="default"
              size="sm"
              onClick={handleVerifyDomain}
              loading={isVerifying}
              disabled={!customDomain}
              iconName="Shield"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              {isVerifying ? 'Verifying...' : 'Verify Domain'}
            </Button>

            {verificationStatus === 'success' && (
              <div className="flex items-start gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
                <Icon name="CheckCircle" size={20} className="text-success mt-0.5 flex-shrink-0" />
                <div>
                  <p className="caption font-medium text-success">Domain Verified</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Your custom domain is now active
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <h4 className="caption font-medium text-foreground">DNS Configuration</h4>
            <p className="text-xs text-muted-foreground">
              Add these DNS records to your domain provider to connect your custom domain
            </p>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-3 caption font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-3 px-3 caption font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-3 caption font-medium text-muted-foreground">Value</th>
                    <th className="text-left py-3 px-3 caption font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dnsRecords?.map((record, index) => (
                    <tr key={index} className="border-b border-border last:border-0">
                      <td className="py-3 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded bg-primary/10 text-primary text-xs font-mono">
                          {record?.type}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm font-mono text-foreground">{record?.name}</td>
                      <td className="py-3 px-3 text-sm font-mono text-foreground break-all">{record?.value}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1">
                          <Icon name="CheckCircle" size={16} className="text-success" />
                          <span className="text-xs text-success capitalize">{record?.status}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="caption font-medium text-foreground">Setup Instructions</p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Log in to your domain provider's dashboard</li>
                  <li>Navigate to DNS settings or DNS management</li>
                  <li>Add the DNS records shown in the table above</li>
                  <li>Wait 24-48 hours for DNS propagation</li>
                  <li>Click "Verify Domain" to check configuration</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          SSL Certificate
        </h3>
        
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Lock" size={24} className="text-success mt-0.5 flex-shrink-0" />
              <div>
                <p className="caption font-medium text-success">SSL Active</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Automatically provisioned and renewed
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
              Renew
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Issued Date</p>
              <p className="caption font-medium text-foreground">Dec 01, 2025</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Expiry Date</p>
              <p className="caption font-medium text-foreground">Mar 01, 2026</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainSettings;